import React from "react";
import { withSafeRender } from "../../atoms/safe-render";

/**
 * Nav glyphs, exported from FUSION ECLIPSE and inlined as plain markup — the
 * path data is the export, not a redraw. `fill` is `currentColor` and the size
 * comes from the stylesheet, so colour and scale stay in the CSS Module.
 * All are decorative: the accessible name lives on the control wrapping them.
 */

type IconProps = { className?: string };

function svgProps(viewBox: string, className?: string) {
  return {
    "aria-hidden": true as const,
    className,
    focusable: "false" as const,
    viewBox,
    xmlns: "http://www.w3.org/2000/svg"
  };
}

function SearchIconImpl({ className }: IconProps) {
  return (
    <svg {...svgProps("0 0 24 24", className)} fill="none">
      <path
        clipRule="evenodd"
        d="M6.47822 5.92614C8.95141 3.45296 12.9614 3.45305 15.4346 5.92614C17.9078 8.39937 17.9078 12.4093 15.4346 14.8825C12.9614 17.3557 8.95145 17.3557 6.47822 14.8825C4.00513 12.4093 4.00504 8.39932 6.47822 5.92614ZM14.225 7.13643C12.4201 5.33157 9.49337 5.33157 7.68851 7.13643C5.88365 8.94129 5.88365 11.868 7.68851 13.6729C9.49337 15.4777 12.4201 15.4777 14.225 13.6729C16.0298 11.868 16.0298 8.94129 14.225 7.13643Z"
        fill="currentColor"
        fillRule="evenodd"
      />
      <path
        d="M14.4335 15.2953C14.0755 14.9373 14.0755 14.3569 14.4335 13.999C14.7915 13.641 15.3719 13.641 15.7299 13.999L20.5618 18.8309C20.9198 19.1888 20.9198 19.7692 20.5618 20.1272C20.2038 20.4852 19.6234 20.4852 19.2654 20.1272L14.4335 15.2953Z"
        fill="currentColor"
      />
    </svg>
  );
}
export const SearchIcon = withSafeRender(SearchIconImpl, "SearchIcon");

function CartIconImpl({ className }: IconProps) {
  return (
    <svg {...svgProps("0 0 24 24", className)} fill="none">
      <path
        d="M20.2035 6.9608L7.45079 6.96417C7.30036 6.31632 7.15855 5.70656 7.0362 5.17842C7.00257 5.03308 6.91976 4.90335 6.80133 4.81038C6.6829 4.71741 6.53597 4.66667 6.38446 4.66667H2.66856C2.49124 4.66667 2.32124 4.73601 2.19587 4.85936C2.07049 4.9827 2 5.14994 2 5.32438C2 5.49882 2.07049 5.66622 2.19587 5.78957C2.32124 5.91291 2.49124 5.98209 2.66856 5.98209H5.85154C7.81041 14.4278 7.85721 14.6331 7.85721 14.6331C7.93618 15.1385 8.13941 15.6172 8.44921 16.0278C8.75902 16.4384 9.16598 16.7684 9.63486 16.989C9.52966 17.2055 9.47465 17.4423 9.47376 17.6822C9.47453 17.9109 9.52397 18.1369 9.61886 18.3457C9.71375 18.5545 9.85206 18.7415 10.0248 18.8946C10.1975 19.0476 10.4009 19.1635 10.6219 19.2346C10.8428 19.3058 11.0764 19.3304 11.3077 19.3074C11.539 19.2843 11.7629 19.214 11.9648 19.1007C12.1668 18.9875 12.3424 18.8339 12.4805 18.6498C12.6185 18.4658 12.7159 18.2554 12.7663 18.0321C12.8167 17.8088 12.819 17.5776 12.7731 17.3534H16.0584C15.9887 17.6785 16.0209 18.0168 16.1508 18.3235C16.2808 18.6303 16.5024 18.891 16.7862 19.0712C17.0701 19.2513 17.4028 19.3425 17.7404 19.3326C18.0781 19.3226 18.4046 19.212 18.677 19.0155C18.9494 18.8189 19.1547 18.5457 19.2657 18.2319C19.3768 17.918 19.3885 17.5784 19.299 17.258C19.2096 16.9375 19.0233 16.6512 18.765 16.4371C18.5066 16.223 18.1883 16.0912 17.8521 16.059C17.7997 16.0456 17.7458 16.0385 17.6916 16.0379H11.7061C10.0762 16.0379 9.51856 15.5164 9.23643 14.6042L18.7066 14.6186H18.7467C18.9998 14.6195 19.2457 14.5363 19.4448 14.3826C19.6439 14.2288 19.7844 14.0135 19.8437 13.7714L21.2912 8.38815L21.3166 8.27623C21.3266 8.21663 21.3322 8.15645 21.3333 8.09606C21.3363 7.79817 21.219 7.51135 21.0072 7.29849C20.7953 7.08563 20.5063 6.96409 20.2035 6.9608Z"
        fill="currentColor"
      />
    </svg>
  );
}
export const CartIcon = withSafeRender(CartIconImpl, "CartIcon");

/**
 * The expanded state keeps the same three bars rather than swapping to a
 * cross, so the button's accessible name and `aria-expanded` are what change.
 */
function MenuIconImpl({ className }: IconProps) {
  return (
    <svg {...svgProps("0 0 32 32", className)} fill="none">
      <path
        d="M24.1778 15.1111C24.5705 15.1111 24.8889 15.5091 24.8889 16C24.8889 16.4909 24.5705 16.8889 24.1778 16.8889H7.82222C7.42949 16.8889 7.11111 16.4909 7.11111 16C7.11111 15.5091 7.42949 15.1111 7.82222 15.1111H24.1778Z"
        fill="currentColor"
      />
      <path
        d="M24.1778 20.4444C24.5705 20.4444 24.8889 20.8424 24.8889 21.3333C24.8889 21.8243 24.5705 22.2222 24.1778 22.2222H7.82222C7.42949 22.2222 7.11111 21.8243 7.11111 21.3333C7.11111 20.8424 7.42949 20.4444 7.82222 20.4444H24.1778Z"
        fill="currentColor"
      />
      <path
        d="M24.1778 9.77778C24.5705 9.77778 24.8889 10.1757 24.8889 10.6667C24.8889 11.1576 24.5705 11.5556 24.1778 11.5556H7.82222C7.42949 11.5556 7.11111 11.1576 7.11111 10.6667C7.11111 10.1757 7.42949 9.77778 7.82222 9.77778H24.1778Z"
        fill="currentColor"
      />
    </svg>
  );
}
export const MenuIcon = withSafeRender(MenuIconImpl, "MenuIcon");

function CloseIconImpl({ className }: IconProps) {
  return (
    <svg {...svgProps("0 0 24 24", className)} fill="none">
      <path
        d="M16.4786 6.34313C16.804 6.01775 17.3316 6.01771 17.657 6.34313C17.9822 6.66856 17.9823 7.19614 17.657 7.52152L13.1785 12L17.657 16.4785C17.9821 16.804 17.9823 17.3316 17.657 17.6569C17.3315 17.9824 16.8034 17.9824 16.4779 17.6569L11.9994 13.1778L7.52155 17.6569C7.19616 17.9822 6.66856 17.9821 6.34316 17.6569C6.01773 17.3315 6.01773 16.8033 6.34316 16.4779L10.8204 11.9994L6.34316 7.52152C6.01773 7.19608 6.01773 6.66857 6.34316 6.34313C6.66861 6.01775 7.19613 6.01771 7.52155 6.34313L11.9994 10.821L16.4786 6.34313Z"
        fill="currentColor"
      />
    </svg>
  );
}
export const CloseIcon = withSafeRender(CloseIconImpl, "CloseIcon");

function ChevronRightIconImpl({ className }: IconProps) {
  return (
    <svg {...svgProps("0 0 16 16", className)} fill="none">
      <path
        d="M6.39885 12.8183C6.18565 13.0315 5.83955 13.0314 5.62628 12.8183L5.58852 12.7766C5.42572 12.5766 5.42585 12.2879 5.58895 12.0878L5.62628 12.0461L9.67272 8.00014L5.62671 3.95413C5.42681 3.75423 5.41452 3.43775 5.58939 3.22323L5.62671 3.18156C5.82655 2.982 6.14271 2.96942 6.35718 3.14423L6.39885 3.18156L10.819 7.6017C10.9278 7.71063 10.9788 7.85356 10.9774 7.99319L10.9778 7.99276C10.978 7.99818 10.9778 8.00382 10.9778 8.00925C10.9786 8.14835 10.9269 8.29008 10.819 8.39814L6.39885 12.8183Z"
        fill="currentColor"
      />
    </svg>
  );
}
export const ChevronRightIcon = withSafeRender(ChevronRightIconImpl, "ChevronRightIcon");
